import { Group, Tooltip } from '@mantine/core';
import {
  IconHash,
  IconBold,
  IconItalic,
  IconUnderline,
  IconLink,
  IconList,
  IconListNumbers,
  IconClearFormatting,
  IconFileSymlink,
} from '@tabler/icons';
import { useCurrentEditor } from '@tiptap/react';
import { FC, useCallback } from 'react';
import ActionIconWithoutFocus from '../../../Atoms/ActionIconWithoutFocus';
import ToolbarHeadingIcon from './icons/ToolbarHeadingIcon';
import ToolbarColorIcon from './icons/ToolbarColorIcon';

interface Props {
  includeListOpts?: boolean;
  includePlaceholers?: boolean;
  fileUrl?: string | null;
  fileName?: string | null;
  showFileIcon?: boolean;
}

const DocumentInputToolbar: FC<Props> = ({
  includeListOpts = false,
  includePlaceholers = true,
  fileUrl,
  fileName,
  showFileIcon = true,
}) => {
  const { editor } = useCurrentEditor();

  const setLink = useCallback(() => {
    if (editor) {
      const previousUrl = editor.getAttributes('link').href;
      // eslint-disable-next-line no-alert
      const url = window.prompt('URL', previousUrl);
      // cancelled
      if (url === null) {
        return;
      }
      // empty
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      // update link
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  const setFileAsLink = () => {
    if (editor && fileUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: fileUrl }).run();
    }
  };

  const onHashClicked = () => {
    if (editor) {
      editor.commands.insertContent('#');
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <Group spacing={0} p="xs">
      <Tooltip openDelay={300} withArrow transition="slide-up" label="Fett">
        <div>
          <ActionIconWithoutFocus
            variant={editor.isActive('bold') ? 'filled' : 'subtle'}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
          >
            <IconBold size={20} />
          </ActionIconWithoutFocus>
        </div>
      </Tooltip>
      <Tooltip openDelay={300} withArrow transition="slide-up" label="Kursiv">
        <div>
          <ActionIconWithoutFocus
            variant={editor.isActive('italic') ? 'filled' : 'subtle'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
          >
            <IconItalic size={20} />
          </ActionIconWithoutFocus>
        </div>
      </Tooltip>
      <Tooltip openDelay={300} withArrow transition="slide-up" label="Unterstrichen">
        <div>
          <ActionIconWithoutFocus
            variant={editor.isActive('underline') ? 'filled' : 'subtle'}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            disabled={!editor.can().chain().focus().toggleUnderline().run()}
          >
            <IconUnderline size={20} />
          </ActionIconWithoutFocus>
        </div>
      </Tooltip>
      <ToolbarHeadingIcon />
      <Tooltip openDelay={300} withArrow transition="slide-up" label="Formatierung löschen">
        <div>
          <ActionIconWithoutFocus
            variant="subtle"
            onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
            disabled={!editor.can().chain().focus().clearNodes().unsetAllMarks().run()}
          >
            <IconClearFormatting size={20} />
          </ActionIconWithoutFocus>
        </div>
      </Tooltip>
      <Tooltip openDelay={300} withArrow transition="slide-up" label="Verlinken">
        <div>
          <ActionIconWithoutFocus variant={editor.isActive('link') ? 'filled' : 'subtle'} onClick={setLink}>
            <IconLink size={20} />
          </ActionIconWithoutFocus>
        </div>
      </Tooltip>
      {includeListOpts && (
        <>
          <Tooltip openDelay={300} withArrow transition="slide-up" label="Liste">
            <div>
              <ActionIconWithoutFocus
                variant={editor.isActive('bulletList') ? 'filled' : 'subtle'}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                disabled={!editor.can().chain().focus().toggleBulletList().run()}
              >
                <IconList size={20} />
              </ActionIconWithoutFocus>
            </div>
          </Tooltip>
          <Tooltip openDelay={300} withArrow transition="slide-up" label="Nummerierte Liste">
            <div>
              <ActionIconWithoutFocus
                variant={editor.isActive('orderedList') ? 'filled' : 'subtle'}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                disabled={!editor.can().chain().focus().toggleOrderedList().run()}
              >
                <IconListNumbers size={20} />
              </ActionIconWithoutFocus>
            </div>
          </Tooltip>
        </>
      )}
      <ToolbarColorIcon />
      {includePlaceholers && (
        <Tooltip openDelay={300} withArrow transition="slide-up" label="Platzhalter einfügen">
          <div>
            <ActionIconWithoutFocus onClick={onHashClicked}>
              <IconHash size={20} />
            </ActionIconWithoutFocus>
          </div>
        </Tooltip>
      )}
      {fileUrl && fileName && showFileIcon && (
        <Tooltip openDelay={300} withArrow transition="slide-up" label={`Verlinken ${fileName}`}>
          <div>
            <ActionIconWithoutFocus onClick={setFileAsLink}>
              <IconFileSymlink size={20} />
            </ActionIconWithoutFocus>
          </div>
        </Tooltip>
      )}
    </Group>
  );
};

export default DocumentInputToolbar;
